import {
  EnvelopeIcon,
  CheckCircleIcon,
  XIcon,
  ArrowsClockwiseIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AgentIcon } from '@/components/common/AgentIcon';

/**
 * Loading state shown while generating a suggestion
 */
interface AiSuggestionLoadingProps {
  className?: string;
}

export function AiSuggestionLoading({ className }: AiSuggestionLoadingProps) {
  return (
    <div
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950',
        className,
      )}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-stone-100 dark:bg-stone-800">
        <AgentIcon state="thinking" size={24} />
      </div>
      <div className="flex flex-1 items-center gap-2">
        <span className="text-sm text-blue-800 dark:text-blue-200">
          Analyzing order and generating AI suggestion...
        </span>
      </div>
    </div>
  );
}

/**
 * Error state shown when suggestion generation failed
 */
interface AiSuggestionErrorProps {
  errorMessage?: string | null;
  onRetry?: () => void;
  isRetrying?: boolean;
  className?: string;
}

export function AiSuggestionError({
  errorMessage,
  onRetry,
  isRetrying = false,
  className,
}: AiSuggestionErrorProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex w-full items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950',
        className,
      )}
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
        <WarningCircleIcon className="h-4 w-4 text-red-600 dark:text-red-400" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-red-900 dark:text-red-100">
          AI Suggestion Failed
        </div>
        <p className="mt-1 text-sm text-red-800 dark:text-red-200">
          {errorMessage || 'An error occurred while generating the suggestion.'}
        </p>
        {onRetry && (
          <div className="mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={onRetry}
              disabled={isRetrying}
              className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-300"
            >
              <ArrowsClockwiseIcon
                className={cn('mr-1 h-3 w-3', isRetrying && 'animate-spin')}
              />
              {isRetrying ? 'Retrying...' : 'Retry'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export interface AiSuggestion {
  id: string;
  suggestionType: 'approve_order' | 'send_email_draft';
  status:
    | 'pending'
    | 'accepted'
    | 'rejected'
    | 'invalidated'
    | 'generating'
    | 'failed';
  summary: string | null;
  actionReason: string | null;
  errorMessage?: string | null;
  emailDraftId?: string | null;
}

interface AiSuggestionBannerProps {
  suggestion: AiSuggestion;
  onReviewDraft?: () => void;
  onAccept: () => void;
  onReject: () => void;
  onRefresh?: () => void;
  isLoading?: boolean;
  isRefreshing?: boolean;
  className?: string;
}

const suggestionTypeConfig = {
  approve_order: {
    title: 'Order Ready for Approval',
    icon: CheckCircleIcon,
    primaryAction: 'Approve Order',
    primaryActionVariant: 'default' as const,
  },
  send_email_draft: {
    title: 'Send Email to Customer',
    icon: EnvelopeIcon,
    primaryAction: 'Review Email Draft',
    primaryActionVariant: 'default' as const,
  },
};

export function AiSuggestionBanner({
  suggestion,
  onReviewDraft,
  onAccept,
  onReject,
  onRefresh,
  isLoading = false,
  isRefreshing = false,
  className,
}: AiSuggestionBannerProps) {
  // Only show banner for pending suggestions
  if (suggestion.status !== 'pending') {
    return null;
  }

  const config = suggestionTypeConfig[suggestion.suggestionType];
  const Icon = config.icon;
  const isApproveOrder = suggestion.suggestionType === 'approve_order';

  const handlePrimaryAction = () => {
    if (
      suggestion.suggestionType === 'send_email_draft' &&
      suggestion.emailDraftId &&
      onReviewDraft
    ) {
      onReviewDraft();
    } else {
      onAccept();
    }
  };

  // Approve order: clean green banner with just title + approve button
  if (isApproveOrder) {
    return (
      <div
        role="alert"
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950',
          className,
        )}
      >
        <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
        <span className="flex-1 font-medium text-green-900 dark:text-green-100">
          Order Ready for Approval
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={onAccept}
          disabled={isLoading}
          className="border-green-300 text-green-700 hover:bg-green-100 dark:border-green-700 dark:text-green-300 dark:hover:bg-green-900"
        >
          <CheckCircleIcon className="h-3.5 w-3.5" />
          Approve Order
        </Button>
      </div>
    );
  }

  // Send email draft: full banner with details
  return (
    <div
      role="alert"
      className={cn(
        'flex w-full items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950',
        className,
      )}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-stone-100 dark:bg-stone-800">
        <AgentIcon state="agent" size={24} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-medium text-blue-900 dark:text-blue-100">
            <Icon className="h-4 w-4 shrink-0" />
            <span>AI Suggestion: {config.title}</span>
          </div>
          {onRefresh && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onRefresh}
              disabled={isLoading || isRefreshing}
              className="h-6 w-6 shrink-0 p-0 text-blue-600 hover:text-blue-800 dark:text-blue-400"
              title="Regenerate suggestion"
            >
              <ArrowsClockwiseIcon
                className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')}
              />
            </Button>
          )}
        </div>
        <p className="mt-1 text-sm text-blue-800 dark:text-blue-200">
          {suggestion.actionReason ||
            suggestion.summary ||
            'AI has analyzed this email and has a suggestion.'}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={config.primaryActionVariant}
            onClick={handlePrimaryAction}
            disabled={isLoading}
          >
            {config.primaryAction}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onAccept}
            disabled={isLoading}
          >
            <CheckCircleIcon className="mr-1 h-3 w-3" />
            Approve Order Instead
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onReject}
            disabled={isLoading}
            className="text-muted-foreground hover:text-destructive"
          >
            <XIcon className="mr-1 h-3 w-3" />
            Dismiss
          </Button>
        </div>
      </div>
    </div>
  );
}
